import { useEffect, useState, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { UserManagement } from "@/components/UserManagement";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, CaretRight, Key, LockSimple, LockSimpleOpen, PaperPlaneRight, CheckCircle, Rocket, Crown } from "@phosphor-icons/react";

const fmtDate = (t) => (t ? new Date(t).toLocaleDateString() : null);
const TIER_LABEL = { medium_onetime: "Medium Team", enterprise_onetime: "Enterprise Team", promo: "Promo" };
const STATUS_STYLES = {
  Anchor: "text-purple-300 border-purple-400/40 bg-purple-400/5",
  Active: "text-green-400 border-green-400/40 bg-green-400/5",
  Inactive: "text-zinc-400 border-zinc-700 bg-zinc-800/30",
  Locked: "text-amber-400 border-amber-400/40 bg-amber-400/5",
};
const StatusBadge = ({ value, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center justify-center min-w-[68px] px-2 py-0.5 border font-mono-p text-[10px] uppercase tracking-wider ${STATUS_STYLES[value] || "text-zinc-500 border-zinc-700"}`}>
    {value || "—"}
  </span>
);

function BuyersTable({ readOnly }) {
  const [buyers, setBuyers] = useState(null);
  const [open, setOpen] = useState(() => new Set());

  const load = useCallback(() => api.get("/admin/buyers").then((r) => setBuyers(r.data)).catch((e) => { toast.error(apiError(e)); setBuyers([]); }), []);
  useEffect(() => { load(); }, [load]);
  const toggle = (i) => setOpen((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });

  return (
    <div className="mt-12">
      <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400 mb-1">// Report</p>
      <h2 className="font-mono-p font-bold tracking-tight text-xl">Buyer &amp; AI iD Card Ledger</h2>
      <p className="mt-1 text-xs text-zinc-500">Click a buyer to expand their Master Keys with Build Keys nested underneath. Empty fields show <span className="text-zinc-600">—</span>.</p>

      <div className="mt-4 border border-zinc-800 overflow-x-auto">
        <table data-testid="buyers-table" className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#0c0e12] font-mono-p text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800 text-left">
              <th className="px-3 py-3 w-8"></th>
              <th className="px-3 py-3">Buyer Email</th>
              <th className="px-3 py-3">Tier</th>
              <th className="px-3 py-3">Users / Tier</th>
              <th className="px-3 py-3">Keys / User</th>
              <th className="px-3 py-3">Member #</th>
              <th className="px-3 py-3 text-right">Total Build Keys</th>
              <th className="px-3 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {buyers === null && <tr><td colSpan={8} className="px-6 py-10 text-center text-zinc-600 font-mono-p animate-pulse">LOADING LEDGER...</td></tr>}
            {buyers && buyers.length === 0 && <tr><td colSpan={8} className="px-6 py-10 text-center text-zinc-500">No buyers provisioned yet.</td></tr>}
            {buyers && buyers.map((b, i) => (
              <BuyerRow key={b.buyer_email} b={b} i={i} isOpen={open.has(i)} onToggle={() => toggle(i)} onChanged={load} readOnly={readOnly} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BuyerRow({ b, i, isOpen, onToggle, onChanged, readOnly }) {
  return (
    <>
      <tr data-testid={`buyer-row-${i}`} onClick={onToggle} className="border-b border-zinc-900 cursor-pointer hover:bg-[#0c0e12] transition-colors">
        <td className="px-3 py-3"><CaretRight size={14} data-testid={`buyer-expand-${i}`} className={`text-zinc-500 transition-transform ${isOpen ? "rotate-90 text-cyan-400" : ""}`} /></td>
        <td className="px-3 py-3 text-zinc-200 whitespace-nowrap">{b.buyer_email}</td>
        <td className="px-3 py-3 text-zinc-300 whitespace-nowrap">{TIER_LABEL[b.tier] || b.tier || "—"}</td>
        <td className="px-3 py-3 text-zinc-300">{b.users_per_tier ?? "—"}</td>
        <td className="px-3 py-3 text-zinc-300">{b.keys_per_user ?? "—"}</td>
        <td className="px-3 py-3 font-mono-p text-cyan-300 whitespace-nowrap">{b.member_number || <span className="text-zinc-700">—</span>}</td>
        <td className="px-3 py-3 text-right font-mono-p text-cyan-300">{b.total_build_keys}</td>
        <td className="px-3 py-3">{b.suspended ? <span className="text-red-400 text-xs font-mono-p uppercase">Suspended</span> : <span className="text-green-400 text-xs font-mono-p uppercase">Active</span>}</td>
      </tr>
      {isOpen && (
        <tr className="bg-black/40 border-b border-zinc-900">
          <td colSpan={8} className="px-6 py-4">
            {b.master_keys.length === 0 && b.direct_build_keys.length === 0 && <p className="text-xs text-zinc-500">No keys.</p>}
            {b.master_keys.map((m) => <MasterKeyBlock key={m.id} m={m} onChanged={onChanged} readOnly={readOnly} />)}
            {b.direct_build_keys.length > 0 && (
              <div data-testid={`buyer-${i}-promo`} className="border border-zinc-800 bg-[#0b0e13] p-3 mb-2">
                <p className="font-mono-p text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Promo — Build Keys (no Master Key)</p>
                <div className="flex flex-wrap gap-2">
                  {b.direct_build_keys.map((bk, j) => (
                    <span key={j} data-testid={`promo-build-${i}-${j}`} className="inline-flex items-center gap-2 border border-zinc-800 bg-black px-2 py-1">
                      <Key size={12} className="text-zinc-600" /><span className="font-mono-p text-xs text-zinc-300">{bk.key}</span>
                      <StatusBadge value={bk.status} />
                    </span>
                  ))}
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function MasterKeyBlock({ m, onChanged, readOnly }) {
  const [email, setEmail] = useState(m.recipient_email || "");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!email.trim()) { toast.error("Enter a recipient email"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/master-keys/${m.id}/send-link`, { email: email.trim() });
      toast.success(`Registration link sent to ${email.trim()}`);
      onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`master-${m.id}`} className="border border-cyan-400/20 bg-[#0b0e13] p-3 mb-2">
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-2"><Key size={14} className="text-cyan-400" /><span className="font-mono-p text-sm text-cyan-300">{m.key}</span></span>
        <span className={`inline-flex items-center gap-1 font-mono-p text-[10px] uppercase tracking-wider px-2 py-0.5 border ${m.reg_link_sent ? "text-green-400 border-green-400/40" : "text-zinc-500 border-zinc-700"}`}>
          {m.reg_link_sent ? <><CheckCircle size={12} /> Sent</> : "Not Sent"}
        </span>
        {m.registered && <span className="font-mono-p text-[10px] uppercase text-green-400">Registered · {m.member_number || "MN —"}</span>}
        {m.recipient_email && <span className="text-xs text-zinc-500">→ {m.recipient_email}</span>}
        {!readOnly && (
        <div className="ml-auto flex items-center gap-2">
          <input data-testid={`master-email-${m.id}`} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="recipient email" type="text" name={`mk-recipient-${m.id}`} autoComplete="off" data-lpignore="true" data-1p-ignore="true" data-form-type="other" className="bg-black border border-zinc-800 px-2 py-1.5 text-xs text-zinc-200 focus:border-cyan-400/60 focus:outline-none w-48" />
          <button data-testid={`master-send-${m.id}`} disabled={busy} onClick={send} className="inline-flex items-center gap-1.5 font-mono-p text-[10px] uppercase tracking-widest bg-cyan-400 text-black px-3 py-1.5 font-bold hover:bg-cyan-300 transition-colors disabled:opacity-50">
            <PaperPlaneRight size={12} weight="bold" /> {m.reg_link_sent ? "Resend" : "Send Link"}
          </button>
        </div>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-2 pl-6">
        {m.build_keys.map((bk, j) => (
          <span key={j} data-testid={`build-${m.id}-${j}`} className="inline-flex items-center gap-2 border border-zinc-800 bg-black px-2 py-1">
            <span className="font-mono-p text-xs text-zinc-400">{bk.key}</span>
            <StatusBadge value={bk.status} />
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.super_admin;
  const isStaff = isAdmin || user?.role === "support";
  const [plan, setPlan] = useState("solo_onetime");
  const [count, setCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [provisioning, setProvisioning] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/keys/generate", { plan, count: Number(count) });
      toast.success(`${data.created.length} key(s) generated`);
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const provisionLaunch = async () => {
    if (!window.confirm("Provision the launch accounts (creates missing accounts + generates MN, Master Keys and Build Keys)? This is idempotent.")) return;
    setProvisioning(true);
    try {
      const { data } = await api.post("/admin/provision-launch");
      const made = data.provisioned.filter((r) => !r.skipped).length;
      toast.success(made ? `Provisioned ${made} account(s)` : "All accounts already provisioned");
    } catch (err) { toast.error(apiError(err)); } finally { setProvisioning(false); }
  };

  const grantGod = async () => {
    const email = window.prompt("Enable god mode (full super-admin access) for which account?", "domingo@test.com");
    if (!email) return;
    try {
      const { data } = await api.post("/admin/grant-god", { email: email.trim() });
      toast.success(`God mode enabled for ${data.email}${data.created ? " — account created, set password on first login" : ""}`);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 pt-28 pb-20">
        {isStaff && (<>
          <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400 mb-2">// {isAdmin ? "Admin — License Keys" : "Support — Read Only"}</p>
          <h1 className="font-mono-p font-bold uppercase tracking-tight text-3xl border-b border-zinc-800 pb-8">Key &amp; Card Management</h1>

          {isAdmin && (
          <div className="mt-8 border border-zinc-800 bg-[#090A0C] p-6 flex flex-wrap items-end gap-4">
            <div>
              <label className="font-mono-p text-xs uppercase tracking-widest text-zinc-500">Plan</label>
              <select data-testid="keygen-plan" value={plan} onChange={(e) => setPlan(e.target.value)} className="mt-2 block bg-black border border-zinc-800 px-4 py-2.5 text-sm text-zinc-100 focus:border-cyan-400/60 focus:outline-none">
                <option value="solo_onetime">Solopreneur (1 key · 1 card)</option>
                <option value="small_onetime">Small Team (1 key · 3 cards)</option>
                <option value="medium_onetime">Medium Team (3 keys · 3 cards)</option>
                <option value="enterprise_onetime">Enterprise (5 keys · 5 cards)</option>
              </select>
            </div>
            <div>
              <label className="font-mono-p text-xs uppercase tracking-widest text-zinc-500">Count</label>
              <input data-testid="keygen-count" type="number" min={1} max={100} value={count} onChange={(e) => setCount(e.target.value)} className="mt-2 block w-24 bg-black border border-zinc-800 px-4 py-2.5 text-sm text-zinc-100 focus:border-cyan-400/60 focus:outline-none" />
            </div>
            <button data-testid="keygen-submit" onClick={generate} disabled={loading} className="flex items-center gap-2 font-mono-p text-sm uppercase tracking-widest bg-cyan-400 text-black px-5 py-2.5 font-bold hover:bg-cyan-300 transition-colors disabled:opacity-50">
              <Plus size={16} weight="bold" /> Generate
            </button>
            <button data-testid="provision-launch-btn" onClick={provisionLaunch} disabled={provisioning} className="flex items-center gap-2 font-mono-p text-sm uppercase tracking-widest border border-cyan-400/40 text-cyan-400 px-5 py-2.5 font-bold hover:bg-cyan-400/10 transition-colors disabled:opacity-50">
              <Rocket size={16} weight="bold" /> {provisioning ? "Provisioning…" : "Provision Launch Accounts"}
            </button>
            <button data-testid="grant-god-btn" onClick={grantGod} className="flex items-center gap-2 font-mono-p text-sm uppercase tracking-widest border border-purple-400/40 text-purple-300 px-5 py-2.5 font-bold hover:bg-purple-400/10 transition-colors">
              <Crown size={16} weight="bold" /> Enable God Mode
            </button>
          </div>
          )}

          <BuyersTable readOnly={!isAdmin} />
        </>)}
        <UserManagement />
      </main>
    </div>
  );
}
